<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import powerDataAPI from 'dashboard/api/powerData';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  storeId: { type: [String, Number], required: true },
  source: { type: Object, default: null },
});
const emit = defineEmits(['saved']);

const { t } = useI18n();
const appStore = useStore();
const accountId = appStore.getters.getCurrentAccountId;

const dialogRef = ref(null);
const filename = ref('');
const tags = ref([]);
const content = ref('');
const contentOriginal = ref('');
const chunksCount = ref(0);
const isLoadingContent = ref(false);
const isLoading = ref(false);
const isSuggesting = ref(false);
const tagInputKey = ref(0);

const contentChanged = computed(
  () => content.value.trim() !== contentOriginal.value.trim()
);

// A lista traz a source sem o texto — 18 KB por linha não cabem numa tabela.
// O texto vem sob demanda, ao abrir a edição.
async function carregarConteudo() {
  if (!props.source?.id) return;
  isLoadingContent.value = true;
  try {
    const data = await powerDataAPI.sourcesShow(
      accountId,
      props.storeId,
      props.source.id
    );
    const texto =
      data?.extracted_text ||
      (data?.chunks || []).map(c => c.content).join('\n\n') ||
      '';
    content.value = texto;
    contentOriginal.value = texto;
    chunksCount.value = (data?.chunks || []).length;
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.FETCH_ERROR'));
  } finally {
    isLoadingContent.value = false;
  }
}

watch(
  () => props.source,
  val => {
    filename.value = val?.filename || '';
    tags.value = [...(val?.tags || [])];
    content.value = '';
    contentOriginal.value = '';
    chunksCount.value = val?.chunks_count || 0;
  },
  { immediate: true }
);

// Sugere, não grava. Um modelo barato erra de vez em quando, e tag errada
// envenena a busca em silêncio — ela entra no índice como se fosse conteúdo.
// A sugestão entra no campo, editável, e só vira índice ao Salvar.
async function sugerirTags() {
  if (!props.source?.id) return;
  isSuggesting.value = true;
  try {
    const data = await powerDataAPI.sourcesSuggestTags(
      accountId,
      props.storeId,
      props.source.id
    );
    const sugeridas = data?.tags || [];
    if (!sugeridas.length) {
      useAlert(t('POWER_DATA.SOURCES.SUGGEST_EMPTY'));
      return;
    }
    tags.value = [...new Set([...tags.value, ...sugeridas])];
    // O TagInput copia o array uma vez, no setup: sem remontar, as tags novas
    // não apareceriam no campo mesmo estando no modelo.
    tagInputKey.value += 1;
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.SUGGEST_ERROR'));
  } finally {
    isSuggesting.value = false;
  }
}

async function submit() {
  if (!props.source?.id) return;
  isLoading.value = true;
  try {
    const payload = { filename: filename.value.trim(), tags: tags.value };
    // Só manda o texto se mudou: é o único campo cuja gravação custa API.
    if (contentChanged.value) payload.content = content.value;
    await powerDataAPI.sourcesUpdate(
      accountId,
      props.storeId,
      props.source.id,
      payload
    );
    useAlert(
      contentChanged.value
        ? t('POWER_DATA.SOURCES.SAVED_REINDEX')
        : t('POWER_DATA.SOURCES.SAVED')
    );
    emit('saved');
    dialogRef.value?.close();
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SOURCES.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

defineExpose({
  // O pai faz `editingSource = row` e chama open() no mesmo tick; a prop só
  // chega depois. Sem esperar, carregarConteudo() via `props.source` ainda
  // vazio e desistia — e o diálogo dizia "sem texto" para uma fonte de 18 KB.
  open: async () => {
    dialogRef.value?.open();
    await nextTick();
    carregarConteudo();
  },
  close: () => dialogRef.value?.close(),
});
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    :title="t('POWER_DATA.SOURCES.EDIT_TITLE')"
    :confirm-button-label="t('POWER_DATA.SAVE')"
    :cancel-button-label="t('POWER_DATA.CANCEL')"
    :disable-confirm-button="!filename.trim() || isLoadingContent"
    :is-loading="isLoading"
    overflow-y-auto
    @confirm="submit"
  >
    <div class="flex flex-col gap-5">
      <Input
        v-model="filename"
        :label="t('POWER_DATA.SOURCES.NAME')"
        :placeholder="t('POWER_DATA.SOURCES.NAME_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <label class="text-body-main text-n-slate-12">
            {{ t('POWER_DATA.SOURCES.CONTENT') }}
          </label>
          <span
            v-if="!isLoadingContent && content"
            class="text-body-small text-n-slate-10 tabular-nums"
          >
            {{
              t('POWER_DATA.SOURCES.CONTENT_STATS', {
                chunks: chunksCount,
                chars: content.length.toLocaleString(),
              })
            }}
          </span>
        </div>
        <div
          v-if="isLoadingContent"
          class="flex items-center gap-2 px-3 py-8 text-body-small text-n-slate-11 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <i class="i-lucide-loader-2 animate-spin" />
          {{ t('POWER_DATA.SOURCES.CONTENT_LOADING') }}
        </div>
        <p
          v-else-if="!content"
          class="px-3 py-8 text-center text-body-small text-n-slate-11 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          {{ t('POWER_DATA.SOURCES.CONTENT_EMPTY') }}
        </p>
        <TextArea
          v-else
          v-model="content"
          custom-text-area-class="font-mono text-xs leading-relaxed"
          auto-height
          min-height="14rem"
          max-height="26rem"
          resize
        />
        <p
          class="text-body-small"
          :class="contentChanged ? 'text-n-amber-11' : 'text-n-slate-11'"
        >
          {{ t('POWER_DATA.SOURCES.CONTENT_HINT') }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <div class="flex items-center justify-between gap-2">
          <label class="text-body-main text-n-slate-12">
            {{ t('POWER_DATA.SOURCES.TAGS') }}
          </label>
          <Button
            :label="t('POWER_DATA.SOURCES.SUGGEST')"
            type="button"
            icon="i-lucide-sparkles"
            slate
            xs
            :is-loading="isSuggesting"
            @click="sugerirTags"
          />
        </div>
        <div
          class="px-3 py-2 transition-colors border rounded-lg border-n-weak bg-n-alpha-1 focus-within:border-n-brand"
        >
          <TagInput
            :key="`${source?.id}-${tagInputKey}`"
            v-model="tags"
            allow-create
            :auto-open-dropdown="false"
            :placeholder="t('POWER_DATA.SOURCES.TAGS_PLACEHOLDER')"
          />
        </div>
        <p class="text-body-small text-n-slate-11">
          {{ t('POWER_DATA.SOURCES.TAGS_HINT') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
